import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {EditMessageBoxProps} from './edit-message-box.types';
import styles from './edit-message-box.module.scss';
import {Button, Size, Type} from '@Components/button';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {autoResizeTextarea} from '@Utils/dom.util';
import {GA4EventName} from '@Libraries/ga-events';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';

export function EditMessageBox({
  value,
  onChange,
  onCancel,
  onSend,
  helpText = window.i18next.t('pmwjs_edit_message_delete_warning'),
  className = '',
  isLoading = false,
  promptBoxId,
}: EditMessageBoxProps): ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [refAttached, setRefAttached] = useState(false);
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(textareaRef, refAttached);
  const {state} = useAiPromptBox(promptBoxId);

  useDidUpdateEffect(() => {
    if (state.promptBoxState === PromptBoxState.ACTIVE) {
      onCancel();
    }
  }, [state.promptBoxState, onCancel]);

  const handleClick = (): void => {
    const trimmedValue = (value || '').trim();

    if (!trimmedValue || isLoading) {
      return;
    }

    onSend();
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_EDIT_MESSAGE);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleClick();
    }
  };

  useDidUpdateEffect(() => {
    if (textareaRef.current) {
      autoResizeTextarea(textareaRef.current);
    }
  }, [value]);

  useEffect(() => {
    if (refAttached && textareaRef.current) {
      const el = textareaRef.current;
      setTimeout(() => {
        el.focus();
        const length = el.value?.length || 0;
        el.setSelectionRange(length, length);
      }, 0);
    }
  }, [refAttached]);

  return (
    <div className={`${styles.root} ${className}`} onKeyDown={handleKeyDown}>
      <InputField
        ref={(el): void => {
          textareaRef.current = el as HTMLTextAreaElement;
          if (el) {
            setRefAttached(true);
            autoResizeTextarea(el as HTMLTextAreaElement);
          }
        }}
        useEmptyPlaceholder
        inputClassName={`${styles.textarea} ${topShadow ? styles.topShadowInset : ''} ${bottomShadow ? styles.bottomShadowInset : ''}`}
        className={'radius-12 _overflow-hidden'}
        input={value}
        isMultiline
        maxNumberOfRows={5}
        size={InputFieldSize.MEDIUM}
        maxLength={10000}
        elementTitle={window.i18next.t('pmwjs_edit')}
        onInputChange={(val): void => {
          onChange(String(val));
        }}
        isDisabled={isLoading}
      />
      <div className={styles.footer}>
        <Text className={`${styles.helpText} content-sub-text`} size={TextSize.XXSMALL} val={helpText} />
        <div className={styles.actions}>
          <Button size={Size.XSMALL} type={Type.SECONDARY} text={window.i18next.t('pmwjs_cancel')} onClick={onCancel} isLoading={isLoading} />
          <Button
            data-testid={AiChatTestId.EDIT_MESSAGE_SEND}
            size={Size.XSMALL}
            type={Type.PRIMARY}
            text={window.i18next.t('pmwjs_send')}
            disabled={!value}
            onClick={handleClick}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
